/* El gasoil en Trelew: el precio de YPF Ruta menos el descuento de Nico.
 *
 * Igna, 06/10/2026: «Nico me reclama el valor del combustible de Trelew. Ése
 * sí es el valor de YPF en ruta −4%». En Caleta el número lo declara Carlos
 * Becerra y se paga tal cual (ver `ypf.js`); en Trelew no hay quien lo
 * declare, y lo que se paga es el precio de lista de YPF Ruta menos el
 * porcentaje de `ypf_descuento`. Son dos lugares y dos maneras de llegar al
 * número: por eso esto vive aparte y no toca nada de lo de Caleta.
 *
 * ── DE DÓNDE SALE: LA ESTACIÓN MICA, Y NINGUNA OTRA ────────────────────────
 *
 * `ypf_precios` lo llena el scraper del simulador de YPF Ruta: una fila por
 * estación por día, con `gasoil_g2` (D-500, código YPF 08) y `gasoil_g3`
 * (Infinia Diesel, 06). Trelew tiene seis estaciones y no siempre al mismo
 * precio (el 07/10: cinco a $2.355 y MICA SA a $2.329 en D-500).
 *
 * Nico, 07/10/2026 (por Igna): «el precio de Trelew es de la estación MICA.
 * De ninguna otra. Sin sacar promedio de nada.» Hasta ese día acá se
 * promediaban las seis del último día; ahora el número de Trelew es el de
 * MICA SA (`TRELEW_ESTACION`), sola, buscada por código de estación y no por
 * nombre: el nombre lo escribe el simulador como quiere, el código no cambia.
 * Las otras cinco no entran en ninguna cuenta, ni para completar un producto
 * que MICA no informó: ese producto queda sin precio y se dice.
 *
 * Si MICA no salió en el último barrido de Trelew, se muestra SU último dato
 * con su fecha a la vista («último dato del dd/mm»), nunca el de otra
 * estación del día: un precio de MICA de anteayer es el precio de MICA; uno
 * de otra estación de hoy no lo es. Sin ninguna fila de MICA, no hay precio.
 *
 * El descuento es la fila de `ypf_descuento` que corresponde a cada producto,
 * con la misma regla que la migración 20261003033000 (ver `porcentajeTrelew`).
 *
 * ── LA CUENTA, Y DÓNDE SE REDONDEA ─────────────────────────────────────────
 *
 *   lista  = el precio de MICA, redondeado al peso
 *   pagado = lista / (1 + porcentaje / 100), redondeado al peso
 *
 * El «4%» de YPF en Ruta se saca DIVIDIENDO por 1,04 (Nico, 26/10/2026,
 * ticket 519, su planilla «4 % yer»: Trelew $2.388 → $2.296,15). Hasta ese
 * día se multiplicaba por 0,96 y salía unos $4 más barato de lo que se paga.
 * Es la misma cuenta que el bot (`ypf_aviso_precios.con_descuento`).
 *
 * Al peso y no a centavos: el simulador da precios enteros, y mostrarle
 * centavos sería prometer una precisión que el dato no tiene (si un día
 * trae «2329.40», la lista es $2.329). Y el descuento se aplica sobre la
 * lista YA REDONDEADA, la que dice la tarjeta: quien agarre la calculadora y
 * haga «lista −4%» tiene que llegar al número grande. El 07/10 da D-500
 * $2.239 (lista $2.329) e Infinia Diesel $2.457 (lista $2.555).
 *
 * ── SI EL DESCUENTO NO ESTÁ ────────────────────────────────────────────────
 *
 * NO SE ASUME 0%. Si `ypf_descuento` no se pudo leer, o se leyó y no hay
 * ninguna fila que aplique al producto, el precio que se muestra es el de
 * lista y se dice que es el de lista. Tomarlo como 0% sería mostrar el de
 * lista con cara de «lo que pagamos», y alguien lo anotaría como tal.
 */

import { codEess, instante } from "./ypf";

/* La localidad, como la escribe el simulador («TRELEW»). Se compara sin
 * mayúsculas ni tildes: si un día el scraper la escribe «Trelew», es la misma. */
export const LOCALIDAD = "Trelew";

/* LA estación de Trelew: MICA SA, código YPF 29041. Nico, 07/10/2026: «el
 * precio de Trelew es de la estación MICA. De ninguna otra. Sin sacar
 * promedio de nada.» Se compara con `codEess`, sin los ceros de adelante, y
 * la consulta de `services/ypf.js` filtra por este mismo código. */
export const TRELEW_ESTACION = "29041";
export const TRELEW_ESTACION_NOMBRE = "MICA";

/* Los dos gasoils, en el orden de la pantalla: D-500 primero. `columna` es la
 * de `ypf_precios`; `producto` es el código YPF con el que se busca el
 * descuento (el mismo de `ypf_precio_pagado` y del bot). */
export const PRODUCTOS_TRELEW = [
  { producto: "08", nombre: "D-500", columna: "gasoil_g2" },
  { producto: "06", nombre: "Infinia Diesel", columna: "gasoil_g3" },
];

/* Por qué un producto quedó sin descuento. Dos motivos y no uno: «no se pudo
 * leer» manda a avisar que algo está roto; «no hay cargado» manda a cargarlo. */
export const SIN_DESCUENTO = {
  NO_SE_PUDO: "no-se-pudo",
  NO_HAY: "no-hay",
};

const norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase();

export const esTrelew = (localidad) => norm(localidad) === norm(LOCALIDAD);

/* Un campo de la regla vacío quiere decir «cualquiera». `""` cuenta como
 * vacío: una celda borrada a mano en el panel de Supabase queda así. */
const vacio = (x) => x === null || x === undefined || String(x).trim() === "";

/* Un precio del simulador, o null. `numeric` puede llegar como número o como
 * texto («2329.0»); vacío, ilegible o cero no es un precio: un cero sería
 * mostrar «$0» por un producto que MICA no informó. */
export function precioDe(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const esMica = (f) => !!f && codEess(f.cod_eess) === TRELEW_ESTACION;

const fechaDe = (f) => {
  const m = /^\d{4}-\d{2}-\d{2}/.exec(String(f?.fecha ?? ""));
  return m ? m[0] : null;
};

/* La última fila de MICA, o null si no hay ninguna.
 *
 * `fecha` es un `date` («2026-10-07»): como texto ISO se ordena bien sin
 * pasar por `Date`, que lo leería como medianoche UTC y en la Argentina sería
 * el día anterior. Las filas de las otras estaciones se ignoran aunque sean
 * más nuevas: NO SE PROMEDIA NI SE REEMPLAZA (Nico, 07/10/2026). */
export function ultimaDeMica(filas) {
  return (filas || [])
    .filter((f) => esMica(f) && fechaDe(f))
    .reduce((a, f) => (!a || fechaDe(f) > fechaDe(a) ? f : a), null);
}

/* El último día con datos de Trelew en las filas que hay (de cualquier
 * estación), o null. Sólo sirve para saber si el dato de MICA está atrasado;
 * ningún precio sale de acá. */
export function ultimoDiaTrelew(filas) {
  return (filas || [])
    .filter((f) => f && esTrelew(f.localidad))
    .map(fechaDe)
    .filter(Boolean)
    .reduce((a, b) => (!a || b > a ? b : a), null);
}

/* Hasta qué momento cuenta un descuento para el precio de `fecha`: el final
 * de ese día en hora argentina (UTC-3 todo el año), o `ahora` si es antes.
 * Así un descuento que empezó a valer ese mismo día se aplica, uno cargado
 * para mañana no, y el precio de un día viejo (si el scraper se quedó) se lee
 * con el descuento que valía ese día y no con el de hoy. */
export function hastaCuandoCuenta(fecha, ahora = new Date()) {
  const finDelDia = Date.parse(`${fecha}T00:00:00-03:00`) + 24 * 60 * 60 * 1000 - 1;
  const t = instante(ahora);
  if (Number.isNaN(finDelDia)) return t;
  return Number.isNaN(t) ? finDelDia : Math.min(finDelDia, t);
}

/* El porcentaje de `ypf_descuento` para un producto en Trelew, o null si no
 * hay ninguno que aplique. NULL, NO 0: «no hay descuento cargado» y «el
 * descuento es 0%» no son lo mismo, y sólo el segundo autoriza a decir que
 * el precio de lista es lo que se paga.
 *
 * LA REGLA, la de la migración 20261003033000:
 *   · Cuentan las filas con `vigente_desde` <= `cuando`.
 *   · El producto, vacío (todos) o el mismo código.
 *   · La estación, SÓLO las generales (`cod_eess` vacío). Así estaba y así
 *     sigue: el pedido del 07/10 cambió de dónde sale la lista (MICA), no
 *     la regla del descuento. Una regla para una estación puntual
 *     (Otamendi, por ejemplo) no es la de Trelew.
 *   · Gana la del producto sobre la de todos los productos y, entre iguales,
 *     la más reciente.
 * Una fila con fecha o porcentaje ilegible se ignora: aplicar un descuento
 * que no se sabe desde cuándo vale, o de cuánto es, es inventar un precio. */
export function porcentajeTrelew(descuentos, producto, cuando) {
  // `cuando` puede venir en milisegundos (de `hastaCuandoCuenta`): `instante`
  // lee Date y texto, y un número pasado por texto no es una fecha.
  const t = typeof cuando === "number" ? cuando : instante(cuando);
  if (Number.isNaN(t)) return null;
  let mejor = null;
  (descuentos || []).forEach((d) => {
    if (!d || !vacio(d.cod_eess)) return;
    const desde = instante(d.vigente_desde);
    const pct = vacio(d.porcentaje) ? NaN : Number(d.porcentaje);
    if (Number.isNaN(desde) || desde > t || !Number.isFinite(pct)) return;
    const porProducto = !vacio(d.producto);
    if (porProducto && String(d.producto).trim() !== producto) return;
    const peso = porProducto ? 1 : 0;
    if (!mejor || peso > mejor.peso || (peso === mejor.peso && desde > mejor.desde)) {
      mejor = { peso, desde, pct };
    }
  });
  return mejor ? mejor.pct : null;
}

/* El precio menos el descuento, al peso: `precio × 100 / (100 + %)`, que es
 * dividir por 1,04 para el 4% (ver «LA CUENTA»). 2388 → 2296; 2458 → 2363. */
export const menosDescuento = (precio, porcentaje) =>
  Math.round((Number(precio) * 100) / (100 + Number(porcentaje)));

/* El porcentaje de `ypf_descuento` para Otamendi (lo que declara Carlos
 * Becerra, ticket 519), o null si no hay ninguno. La regla del bot
 * (`ypf_aviso_precios.descuento_para`) preguntada con la estación de Carlos:
 * cuentan las generales (`cod_eess` vacío) y las de Otamendi, gana la del
 * producto sobre la de todos y la de la estación sobre la general, y entre
 * iguales la más reciente. Hoy aplica el 4% general, el mismo de Trelew. */
export const COD_OTAMENDI = "001501";
export function porcentajeOtamendi(descuentos, producto, cuando) {
  const t = typeof cuando === "number" ? cuando : instante(cuando);
  if (Number.isNaN(t)) return null;
  const sinCeros = (c) => String(c ?? "").trim().replace(/^0+/, "");
  let mejor = null;
  (descuentos || []).forEach((d) => {
    if (!d) return;
    const conCod = !vacio(d.cod_eess);
    if (conCod && sinCeros(d.cod_eess) !== sinCeros(COD_OTAMENDI)) return;
    const desde = instante(d.vigente_desde);
    const pct = vacio(d.porcentaje) ? NaN : Number(d.porcentaje);
    if (Number.isNaN(desde) || desde > t || !Number.isFinite(pct)) return;
    const porProducto = !vacio(d.producto);
    if (porProducto && String(d.producto).trim() !== producto) return;
    const peso = 2 * porProducto + conCod;
    if (!mejor || peso > mejor.peso || (peso === mejor.peso && desde > mejor.desde)) {
      mejor = { peso, desde, pct };
    }
  });
  return mejor ? mejor.pct : null;
}

/* «06/10», del `date` del simulador, sin pasar por `Date` (ver arriba). */
export const diaMesDeFecha = (fecha) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha ?? ""));
  return m ? `${m[3]}/${m[2]}` : "";
};

/* Todo lo que la tarjeta necesita:
 *
 *   null si no hay ninguna fila de MICA, o
 *   {
 *     fecha,         // la de la fila de MICA que se usa («2026-10-07»)
 *     ultimoDia,     // el último día con datos de Trelew (de cualquiera)
 *     atrasado,      // true si MICA no salió ese último día
 *     estacion,      // { cod: "29041", nombre: "MICA" }
 *     productos: [{
 *       producto, nombre,
 *       lista,       // el de MICA, al peso (null si MICA no lo informó)
 *       porcentaje,  // el de `ypf_descuento`, o null
 *       sinDescuento,// null, o por qué no hay (SIN_DESCUENTO)
 *       pagado,      // lista − porcentaje, al peso; null si no hay porcentaje
 *     }],
 *     porcentaje,    // el de los dos productos si es el mismo, si no null
 *   }
 *
 * `filas` son filas de `ypf_precios`: puede haber de otras estaciones, pero
 * el precio sale SÓLO de la de MICA. `ultimoDia` es el último día de Trelew
 * que dijo la base (`preciosTrelew`); sin él se toma el de las filas.
 * `descuentos` es lo leído de `ypf_descuento`, o null si no se pudo leer. */
export function precioTrelew(filas, descuentos, ahora = new Date(), ultimoDia = null) {
  const mica = ultimaDeMica(filas);
  if (!mica) return null;
  const fecha = fechaDe(mica);
  const dia = [fechaDe({ fecha: ultimoDia }), ultimoDiaTrelew(filas), fecha]
    .filter(Boolean)
    .reduce((a, b) => (b > a ? b : a));
  const cuando = hastaCuandoCuenta(fecha, ahora);
  const productos = PRODUCTOS_TRELEW.map(({ producto, nombre, columna }) => {
    const valor = precioDe(mica[columna]);
    const lista = valor === null ? null : Math.round(valor);
    const porcentaje =
      descuentos === null || descuentos === undefined
        ? null
        : porcentajeTrelew(descuentos, producto, cuando);
    const sinDescuento =
      porcentaje !== null
        ? null
        : descuentos === null || descuentos === undefined
          ? SIN_DESCUENTO.NO_SE_PUDO
          : SIN_DESCUENTO.NO_HAY;
    return {
      producto,
      nombre,
      lista,
      porcentaje,
      sinDescuento,
      pagado: lista === null || porcentaje === null ? null : menosDescuento(lista, porcentaje),
    };
  });
  const pcts = [...new Set(productos.map((p) => p.porcentaje))];
  return {
    fecha,
    ultimoDia: dia,
    atrasado: dia > fecha,
    estacion: { cod: TRELEW_ESTACION, nombre: TRELEW_ESTACION_NOMBRE },
    productos,
    porcentaje: pcts.length === 1 && pcts[0] !== null ? pcts[0] : null,
  };
}

/* «−4%», «−4,5%». El signo es el menos tipográfico, como en el pedido. */
export const menosPorcentaje = (pct) =>
  `−${Number(pct).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`;

/* El título de la tarjeta. Con el descuento a la vista —«Gasoil Trelew · YPF
 * Ruta −4%»— sólo si es UNO y se pudo leer: escrito fijo, el día que se
 * renegocie el título diría un porcentaje y los números otro. */
export const tituloTrelew = (precio) =>
  "Gasoil Trelew · YPF Ruta" +
  (precio?.porcentaje !== null && precio?.porcentaje !== undefined
    ? ` ${menosPorcentaje(precio.porcentaje)}`
    : "");
